import Button from '@mui/material/Button';

import { updateChargeAccount } from '@/api/user';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { RadioToggle } from '@/components/Form/RadioToggle';
import { ActionBar } from '@/components/Layout/Layout.styled';
import { QuestionProps } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionDrawer.interface';
import { QuestionForm } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionForm';

import { useAppSelector } from '@/hooks/redux';
import { useForm } from '@/hooks/useForm';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectUserAccounts } from '@/redux/user-data/selectors';

import { AccountType, AccountTypeStatus } from '@/types/api/account';
import { UserAccountType } from '@/types/user';

import { getDataTestId } from '@/utils/browser';

export function QuestionEXCPrimaryAccount({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const userAccountTypes = useAppSelector(selectUserAccounts);
  const filterAccountTypes = userAccountTypes.filter(
    type =>
      type.type === AccountType.Checking &&
      type.status === AccountTypeStatus.Active,
  );
  const hasOnlyOneAccountType = filterAccountTypes.length === 1;
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<{
    accountNo: string;
  }>({
    defaultValues: {
      accountNo: hasOnlyOneAccountType ? filterAccountTypes[0].accountNo : '',
    },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: updateChargeAccount,
    onFinished: onAnswered,
  });

  const onSubmit = handleSubmit(payload => {
    mutate(payload);
  });

  const getAccountLabel = (type: UserAccountType) => {
    const accountCurrency = type.feeCurrency || type.currency || '';
    const currencyLabel = accountCurrency ? `${accountCurrency} - ` : '';
    const accountNoLabel = type.accountNo ? ` - ${type.accountNo}` : '';
    // Format the account label as "Currency - Account Name - Account Number"
    let accountLabel = `${currencyLabel}${type.name}${accountNoLabel}`;
    return accountLabel;
  };

  const primaryAccountOption = filterAccountTypes.map(type => ({
    label: getAccountLabel(type),
    value: type.accountNo || '',
  }));

  return (
    <QuestionForm
      {...props}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      <RadioToggle
        options={primaryAccountOption}
        control={control}
        name="accountNo"
        {...getDataTestId(`question-drawer-primary-account-radio-group`)}
      />
      <ActionBar>
        <Button
          {...getDataTestId(`question-drawer-primary-account-confirm-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {t('questionDrawer.AccountTypeOrder.button.confirm')}
        </Button>
      </ActionBar>
      <FormErrorLogger formName="QuestionEXCPrimaryAccount" control={control} />
    </QuestionForm>
  );
}
